import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Divider, Flex, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { WhiteBoxBlock } from "ui";
import { CONTRACT_ADDRESS_TYPE, CONTRACT_STATUS_ENUM, UnkValidationResponse } from "interfaces";
import { useStores } from "stores";
import { ContractHeader } from "./ContractHeader";
import { isStatusIncluded, parseErrorMessage } from "utils";
import { getBankByUnk, getStatusMessage } from "services";
import { ActionContractNotifier, ItemInfo } from "components";
import { ContractStepsContainer } from "./ContractSteps/ContractSteps";
import { ContractAmount } from "./ContractAmount";
import { ReactComponent as ForeignAgentCodeIcon } from "../../images/foreign-agent-code.svg";
import { AgentInfoBlock } from "./AgentInfoBlock";
import { ApproveContractButton } from "./ContractOperations/ApproveContractButton";
import { ReturnToDraftButton } from "./ContractOperations/ReturnToDraft";
import { EditContractButton } from "./ContractOperations/EditContractButton";
import { BidEntityByContract, BID_REQUEST_STATUS_ENUM } from "interfaces";
import { ContractTabs } from "./ContractTabs/ContractTabs";
import { ContractEditAddressBtn } from "./ContractEditAddressBtn";
import { CancelDraftBtn } from "./ContractOperations/CancelDraftBtn/CancelDraftBtn";
import { BlockContractBtn, DeclineToDraftBtn, UnblockContractBtn } from "./ContractOperations";

export const ContractInfoPage = observer(() => {
  const { id } = useParams();
  const { contractCard, user, contractBids } = useStores();
  const { isCustomer, isSeniorOperator, isJuniorOperator, isRiskManager } = user.roles;

  const isOperator = isSeniorOperator || isJuniorOperator || isRiskManager;

  const [canDisplayBids, setCanDisplayBids] = useState(false);

  const [unkData, setUnkData] = useState<UnkValidationResponse | undefined>();

  const showCancelDraftBtn =
    isStatusIncluded(contractCard.card.status, [CONTRACT_STATUS_ENUM.DRAFT]) && (isSeniorOperator || isJuniorOperator);
  const showDeclineToDraftBtn =
    isStatusIncluded(contractCard.card.status, [CONTRACT_STATUS_ENUM.DRAFT_DECLINED]) && isCustomer;
  const showBlockContractButton =
    isSeniorOperator &&
    isStatusIncluded(contractCard.card.status, [
      CONTRACT_STATUS_ENUM.ACTIVE,
      CONTRACT_STATUS_ENUM.DRAFT,
      CONTRACT_STATUS_ENUM.DRAFT_DECLINED,
    ]);
  const showUnblockContractButton =
    (isSeniorOperator || isJuniorOperator) &&
    isStatusIncluded(contractCard.card.status, [CONTRACT_STATUS_ENUM.BLOCKED]);

  useEffect(() => {
    if (id) {
      const fetchCardInterval = setInterval(() => {
        contractCard.updateCardById(id);
      }, 30000);

      contractCard.fetchCardById(id).then(() => {
        if (contractCard.card.id) {
          contractBids.setId(contractCard.card.id);
          setCanDisplayBids(true);
        }
      });

      return () => clearInterval(fetchCardInterval);
    }
  }, [contractBids, contractCard, id]);

  useEffect(() => {
    if (canDisplayBids && contractCard.card.status === CONTRACT_STATUS_ENUM.DRAFT && isOperator) {
      const fetchUnkData = async () => {
        await getBankByUnk({ unk: contractCard.card.unk })
          .then((unkData) => {
            setUnkData(unkData);
          })
          .catch((e) => {
            console.error(e);
          });
      };

      fetchUnkData();
    }
  }, [canDisplayBids, contractCard.card.status, contractCard.card.unk, isOperator]);

  const showBidsList = canDisplayBids && (isJuniorOperator || isSeniorOperator || isRiskManager || isCustomer);
  const contract = contractCard.card;
  const { status } = contract;

  const showApproveButton = status === CONTRACT_STATUS_ENUM.DRAFT && (isSeniorOperator || isJuniorOperator);

  const hasOnlyCanceledBids =
    contractBids.allData.length === 0 ||
    (contractBids.allData.length > 0 &&
      contractBids.allData.every(
        (bid: BidEntityByContract) =>
          bid.status === BID_REQUEST_STATUS_ENUM.CANCELED || bid.status === BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDED
      ));

  const showReturnToDraftButton =
    (isJuniorOperator || isSeniorOperator) && status === CONTRACT_STATUS_ENUM.ACTIVE && hasOnlyCanceledBids;

  return (
    <Spin spinning={contractCard.isPending}>
      {contractCard.fetchStatus === "success" && id && (
        <Flex vertical gap="middle">
          <ContractHeader />

          <Flex gap="large" justify="space-between">
            <Flex justify="start">
              <ActionContractNotifier infoPage unkData={unkData} />
            </Flex>
            <Flex gap="small" justify="end" style={{ marginLeft: "auto" }}>
              {showCancelDraftBtn && <CancelDraftBtn />}
              {showDeclineToDraftBtn && <DeclineToDraftBtn />}
              {showUnblockContractButton && <UnblockContractBtn />}
              {showBlockContractButton && <BlockContractBtn />}
              {showReturnToDraftButton && <ReturnToDraftButton />}
              {showApproveButton && <ApproveContractButton />}
            </Flex>
          </Flex>
          {canDisplayBids && (
            <ContractStepsContainer>
              <WhiteBoxBlock>
                <Typography.Text strong className="whitebox-title margin-bottom-lg">
                  Данные договора
                </Typography.Text>
                <Flex vertical gap="middle" id="contract-info-block" className="margin-top-lg" style={{ flex: 1 }}>
                  <ContractAmount contract={contract} />
                  <Flex gap="large">
                    <AgentInfoBlock text={contract.counterpartyName} label="Контрагент" />
                    <AgentInfoBlock
                      text={`${contract.counterpartyCountry} — ${contract.counterpartyCountryCode}`}
                      label="Страна контрагента"
                    />
                  </Flex>

                  {contract.foreignOrganizationCode && (
                    <AgentInfoBlock
                      text={contract.foreignOrganizationCode}
                      label="Код иностранной организации"
                      icon={<ForeignAgentCodeIcon />}
                    />
                  )}
                </Flex>
                <Divider />
                <Flex vertical id="contract-info-block" className="margin-top-lg" style={{ flex: 1 }}>
                  <ItemInfo
                    title="Юридический адрес:"
                    text={
                      <ContractEditAddressBtn
                        text={contract.counterpartyLegalAddress}
                        type={CONTRACT_ADDRESS_TYPE.LEGAL}
                      />
                    }
                  />
                  <ItemInfo
                    title="Фактический адрес:"
                    text={
                      <ContractEditAddressBtn
                        text={contract.counterpartyActualAddress}
                        type={CONTRACT_ADDRESS_TYPE.ACTUAL}
                      />
                    }
                  />
                </Flex>
              </WhiteBoxBlock>
              <ContractTabs contractId={contractCard.card.id} showBidsList={showBidsList} />
              <Flex gap="small" justify="end">
                <EditContractButton />
              </Flex>
            </ContractStepsContainer>
          )}
        </Flex>
      )}
      {contractCard.fetchStatus === "error" && contractCard.fetchError && (
        <Alert
          message={getStatusMessage(contractCard.fetchError.status)}
          description={parseErrorMessage(contractCard.fetchError)}
          type="error"
          showIcon
        />
      )}
    </Spin>
  );
});
